// Push Notification utilities
import api from './api';

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return 'unsupported';
  }
  
  const permission = await Notification.requestPermission();
  return permission;
};

// Check if push notifications are supported
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

// Get VAPID public key from backend
const getVapidPublicKey = async () => {
  try {
    const response = await api.get('/push/vapid-public-key');
    return response.data.publicKey;
  } catch (error) {
    console.error('Failed to get VAPID public key:', error);
    return null;
  }
};

// Convert URL-safe base64 to Uint8Array
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// Register service worker and subscribe to push
export const subscribeToPushNotifications = async () => {
  if (!isPushSupported()) {
    return { success: false, error: 'Push notifications not supported' };
  }
  
  try {
    // Request permission first
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      return { success: false, error: 'Notification permission denied' };
    }
    
    // Register service worker
    const registration = await navigator.serviceWorker.register('/push-sw.js');
    console.log('Service Worker registered:', registration);
    
    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;
    
    // Get VAPID public key
    const vapidPublicKey = await getVapidPublicKey();
    if (!vapidPublicKey) {
      return { success: false, error: 'Failed to get VAPID key' };
    }
    
    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
    
    // Send subscription to backend
    const subscriptionData = subscription.toJSON();
    await api.post('/push/subscribe', {
      subscription: {
        endpoint: subscriptionData.endpoint,
        keys: subscriptionData.keys,
        expirationTime: subscriptionData.expirationTime
      }
    });
    
    console.log('Push subscription successful');
    return { success: true, subscription };
    
  } catch (error) {
    console.error('Push subscription failed:', error);
    return { success: false, error: error.message };
  }
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async () => {
  try {
    const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
    if (registration) {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    }
    
    await api.delete('/push/unsubscribe');
    return { success: true };
  } catch (error) {
    console.error('Push unsubscribe failed:', error);
    return { success: false, error: error.message };
  }
};

// Check push subscription status
export const getPushStatus = async () => {
  try {
    const response = await api.get('/push/status');
    return response.data;
  } catch (error) {
    console.error('Failed to get push status:', error);
    return { enabled: false, vapid_configured: false };
  }
};

// Test push notification
export const testPushNotification = async () => {
  try {
    const response = await api.post('/push/test');
    return { success: true, message: response.data.message };
  } catch (error) {
    console.error('Test push failed:', error);
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

// Check current subscription state
export const isSubscribedToPush = async () => {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
    if (!registration) return false;
    
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    return false;
  }
};

// Local notification utilities (fallback)
export const showNotification = (title, body, options = {}) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    console.log('Cannot show notification');
    return;
  }
  
  const notification = new Notification(title, {
    body,
    icon: '/icons/icon-192x192.png',
    badge: '/icons/icon-72x72.png',
    dir: 'rtl',
    lang: 'ar',
    ...options
  });
  
  return notification;
};

// Play notification sound
export const playNotificationSound = () => {
  try {
    const audio = new Audio('/notification.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (error) {
    console.log('Could not play notification sound');
  }
};

export default {
  requestNotificationPermission,
  isPushSupported,
  subscribeToPushNotifications,
  unsubscribeFromPush,
  getPushStatus,
  testPushNotification,
  isSubscribedToPush,
  showNotification,
  playNotificationSound
};
